import { useMemo } from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import cn from 'classnames';

import { ArticleTaskType } from '@/api';
import config from '@/config';
import Badge from '@/design_system/Badge';
import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCloth from '@/icons/Cloth.svg';
import IconStore from '@/icons/Store.svg';
import IconStoreClient from '@/icons/StoreClient.svg';
import IconTask from '@/icons/Task.svg';
import IconUser from '@/icons/User.svg';
import { ARTICLE_TASK_TYPES } from '@/models/article';
import {
  getRequestorType,
  isInDraftStep,
  RequestWithRelationsFromRequestsList,
} from '@/models/request';
import { useStore } from '@/models/store';
import { isExternalUserForOrganization } from '@/models/user';
import { getScopedBoolean, useWorkflowPublic } from '@/models/workflow';
import { useCurrentSession } from '@/services/auth';
import { useRequestName } from '@/utils/useRequestName';
import useViewPort from '@/utils/useViewport';

export const REQUESTS_PER_PAGE = 20;

// Shorter searches match almost every request and are very expensive for the database
const REQUESTS_SEARCH_MIN_LENGTH = 3;

/**
 * The search actually sent to the API: `undefined` when it is too short to be applied
 */
export const getAppliedRequestsSearch = (search: string): string | undefined => {
  const trimmedSearch = search.trim();

  return trimmedSearch.length >= REQUESTS_SEARCH_MIN_LENGTH ? trimmedSearch : undefined;
};

export const requestorTypeIcons = {
  client: <IconUser />,
  store: <IconStore />,
  'client-via-store': <IconStoreClient />,
};

export const RequestReference = ({
  request,
}: {
  request: RequestWithRelationsFromRequestsList;
}) => {
  const { isMobile } = useViewPort();
  const { _ } = useLingui();

  return (
    <Stack gap="0.25rem" row={isMobile} alignItems={isMobile ? 'center' : 'flex-start'}>
      <Stack
        row={isMobile}
        gap={isMobile ? '0.25rem' : undefined}
        alignItems={isMobile ? 'center' : 'flex-start'}
      >
        <InlineText
          size={isMobile ? '200' : '100'}
          weight={isMobile ? 'medium' : 'regular'}
          style={{ wordBreak: 'break-word' }}
        >
          {request.reference}
        </InlineText>
        {!!request.externalReference && (
          <>
            {isMobile && (
              <InlineText size="200" color="secondary">
                {' '}
                •{' '}
              </InlineText>
            )}
            <Paragraph size="200" color="secondary">
              {request.externalReference}
            </Paragraph>
          </>
        )}
      </Stack>
      {isInDraftStep(request) && (
        <Badge color="neutral" size="x-small">
          {_(msg({ id: 'article.status.draft', message: 'Draft' }))}
        </Badge>
      )}
    </Stack>
  );
};

export const RequestName = ({ request }: { request: RequestWithRelationsFromRequestsList }) => {
  const { currentSession } = useCurrentSession();
  const { data: store } = useStore(request?.storeId);
  const { data: workflow } = useWorkflowPublic(request?.workflowId);
  const isExternalUser = isExternalUserForOrganization(currentSession, request.organization.id);

  const isClientEnabled = getScopedBoolean({
    scopedBoolean: workflow?.config.fields?.request?.client?.enabled,
    credentialsScopes: request.scopes,
  });

  const requestName = useRequestName({ request, isClientEnabled });

  return (
    <Stack row gap="8px" alignItems="center" flexWrap="nowrap">
      {isExternalUser ? (
        <img
          src={`${config.apiUrl}/organizations/${request.organization.slug}/logo?type=square&alias=1`}
          alt=""
          title={request.organization.name}
          style={{ width: 40, height: 40 }}
        />
      ) : (
        <span className="text-secondary" style={{ fontSize: '1.25rem' }}>
          {requestorTypeIcons[getRequestorType(request)]}
        </span>
      )}
      <Stack>
        <Paragraph>
          {requestName.major}
          {!isExternalUser &&
            requestName.major === store?.name &&
            !!store?.organizationConfig?.external && (
              <span>
                {' '}
                (<Trans id="requests.applicant.store.distributor">Distributor</Trans>)
              </span>
            )}
        </Paragraph>
        {!!requestName.minor && (
          <Paragraph size="200">
            {requestName.minor}
            {!isExternalUser &&
              requestName.minor === store?.name &&
              !!store?.organizationConfig?.external && (
                <span>
                  {' '}
                  (<Trans id="requests.applicant.store.distributor">Distributor</Trans>)
                </span>
              )}
          </Paragraph>
        )}
      </Stack>
    </Stack>
  );
};

export const RequestTasks = ({ request }: { request: RequestWithRelationsFromRequestsList }) => {
  const { isMobile } = useViewPort();
  const { _ } = useLingui();

  const tasks = useMemo(() => {
    const tasksCount = request.articles.reduce<Partial<Record<ArticleTaskType, number>>>(
      (acc, article) => {
        const taskType = article.taskType;

        if (taskType) {
          if (acc[taskType]) {
            acc[taskType]++;
          } else {
            acc[taskType] = 1;
          }
        }

        return acc;
      },
      {}
    );

    return ARTICLE_TASK_TYPES.filter((task) => !!tasksCount[task.id]).map((task) => ({
      id: task.id,
      label: `${_(task.label)}${tasksCount[task.id]! > 1 ? ` (x${tasksCount[task.id]})` : ''}`,
      labelVariant: task.labelVariant,
    }));
  }, [_, request.articles]);

  if (isMobile) {
    return (
      <>
        {tasks.map((task) => (
          <Box padding="0.25rem 0.5rem" style={{ flex: 'none' }} key={task.id}>
            <Stack row gap="0.25rem" alignItems="center">
              <IconTask
                style={{ fontSize: '1rem' }}
                className={task.labelVariant === 'danger' ? 'text-danger' : undefined}
              />
              <InlineText size="200" color={task.labelVariant === 'danger' ? 'danger' : undefined}>
                {task.label}
              </InlineText>
            </Stack>
          </Box>
        ))}
      </>
    );
  }

  return (
    <ul style={tasks.length <= 1 ? { listStyle: 'none', padding: 0 } : undefined}>
      {!tasks.length && <li>-</li>}
      {!!tasks.length &&
        tasks.map((task) => (
          <li
            key={task.id}
            className={cn({
              'text-danger': task.labelVariant === 'danger',
            })}
          >
            {task.label}
          </li>
        ))}
    </ul>
  );
};

export const RequestNameCardItem = ({
  request,
}: {
  request: RequestWithRelationsFromRequestsList;
}) => {
  const { currentSession } = useCurrentSession();
  const { data: store } = useStore(request?.storeId);
  const { data: workflow } = useWorkflowPublic(request?.workflowId);
  const isExternalUser = isExternalUserForOrganization(currentSession, request.organization.id);

  const isClientEnabled = getScopedBoolean({
    scopedBoolean: workflow?.config.fields?.request?.client?.enabled,
    credentialsScopes: request.scopes,
  });

  const requestName = useRequestName({ request, isClientEnabled });

  return (
    <Stack style={{ alignItems: 'baseline', minWidth: 0 }}>
      <Paragraph weight="medium" ellipsis style={{ width: '100%' }}>
        {requestName.major}
        {!isExternalUser &&
          requestName.major === store?.name &&
          !!store?.organizationConfig?.external && (
            <span>
              {' '}
              (<Trans id="requests.applicant.store.distributor">Distributor</Trans>)
            </span>
          )}
      </Paragraph>
      {!!requestName.minor && (
        <Paragraph size="200" color="secondary">
          {requestName.minor}
          {!isExternalUser &&
            requestName.minor === store?.name &&
            !!store?.organizationConfig?.external && (
              <span>
                {' '}
                (<Trans id="requests.applicant.store.distributor">Distributor</Trans>)
              </span>
            )}
        </Paragraph>
      )}
    </Stack>
  );
};

export const ArticlesCardItem = ({
  request,
}: {
  request: RequestWithRelationsFromRequestsList;
}) => (
  <Box padding="0.25rem 0.5rem" style={{ flex: 'none' }}>
    <Stack row gap="0.25rem" alignItems="center">
      <IconCloth style={{ fontSize: '16px' }} />
      <InlineText size="200" weight="medium">
        {request.allArticles.length}
      </InlineText>
    </Stack>
  </Box>
);
